"use client";

import { Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { z } from "zod";

import { Button } from "@/shared/ui/buttons/button";
import { translateUi } from "@/shared/lib/ui-translate";
import { Label } from "@/shared/ui/forms/label";
import { Input } from "@/shared/ui/inputs/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/modals/dialog";

// Схема валидации для добавления стрима
const addStreamSchema = z.object({
  channelName: z
    .string()
    .min(4, { error: () => translateUi("litAddStreamModal.imyaKanalaMin4") })
    .max(25, { error: () => translateUi("litAddStreamModal.imyaKanalaMax25") })
    .regex(
      /^[a-zA-Z0-9_]+$/,
      { error: () => translateUi("litAddStreamModal.imyaKanalaSimvoly") },
    )
    .trim(),
});

type AddStreamFormErrors = {
  channelName?: string;
};

interface AddStreamModalProps {
  onAddStream: (channelName: string) => void;
}

export function AddStreamModal({ onAddStream }: AddStreamModalProps) {
  const tUi = useTranslations("Ui");
  const [isOpen, setIsOpen] = useState(false);
  const [streamUrl, setStreamUrl] = useState("");
  const [errors, setErrors] = useState<AddStreamFormErrors>({});

  const handleChannelChange = (value: string) => {
    setStreamUrl(value);

    // Очищаем ошибку при изменении
    if (errors["channelName"]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors["channelName"];
        return newErrors;
      });
    }
  };

  const handleAddStream = () => {
    // Валидация с помощью Zod
    const validationResult = addStreamSchema.safeParse({
      channelName: streamUrl,
    });

    if (!validationResult.success) {
      const channelIssue = validationResult.error.issues.find(
        (issue) => issue.path[0] === "channelName",
      );
      setErrors({
        ["channelName"]: channelIssue?.message ?? tUi("litAddStreamModal.nekorrektnoeImyaKanala"),
      });
      return;
    }

    setErrors({});
    onAddStream(streamUrl);
    setStreamUrl("");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="bg-transparent border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50"
        >
          <Plus className="h-4 w-4 mr-2" />
          {tUi("addStreamModal.dobavitStrim")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{tUi("addStreamModal.dobavitStrim")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="channel-name">{tUi("addStreamModal.imyaKanalaTwitch")}</Label>
            <div className="flex items-center">
              <span className="text-sm text-muted-foreground bg-muted px-3 py-2 border border-r-0 rounded-l-md">
                twitch.tv/
              </span>
              <Input
                id="channel-name"
                type="text"
                placeholder="username"
                value={streamUrl}
                onChange={(e) => handleChannelChange(e.target.value)}
                className={`rounded-l-none ${errors["channelName"] ? "border-red-500" : ""}`}
              />
            </div>
            {errors["channelName"] && (
              <p className="text-sm text-red-500">{errors["channelName"]}</p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              {tUi("addStreamModal.otmena")}
            </Button>
            <Button
              onClick={handleAddStream}
              disabled={!streamUrl || !!errors["channelName"]}
              variant="outline"
              className="bg-transparent border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50"
            >
              <Plus className="h-4 w-4 mr-2" />
              {tUi("addStreamModal.dobavit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
